// src/components/providerAssignments/ContactAssignedProvidersSection.tsx
//
// Contact-scoped assigned-providers section for ContactDetailSlideOver.
// Shows all assignments where contactId = this contact. Includes an
// "Assign Provider" action gated by assignments.create.
//
// This section subscribes to PROVIDER_ASSIGNMENT_* events for the
// workspace so the list refreshes when other users create/reassign/end
// assignments — no manual refetch needed.

import { useState } from 'react';
import { UserPlus } from 'lucide-react';
import { useHasCapability } from '../../services/centralContext';
import { useListProviderAssignmentsQuery } from '../../api/providerAssignmentsApi';
import { useProviderAssignmentRealtime } from '../../hooks/useProviderAssignmentRealtime';
import AssignmentsTable from './AssignmentsTable';
import AssignmentWizard from './AssignmentWizard';

export interface ContactAssignedProvidersSectionProps {
  contactId: string;
  workspaceId?: string;
}

export default function ContactAssignedProvidersSection({
  contactId,
  workspaceId,
}: ContactAssignedProvidersSectionProps) {
  const canView = useHasCapability('assignments', 'view');
  const canCreate = useHasCapability('assignments', 'create');
  const [showWizard, setShowWizard] = useState(false);

  useProviderAssignmentRealtime({ workspaceId, contactId });

  const { data: rows = [], isLoading, refetch } = useListProviderAssignmentsQuery(
    { workspaceId, contactId },
    { skip: !contactId || !workspaceId || !canView },
  );

  if (!canView) return null;

  return (
    <section data-testid="contact-assigned-providers">
      <div className="mb-2 flex items-center justify-between">
        <h4 className="flex items-center gap-2 text-sm font-semibold text-gray-900">
          <UserPlus className="h-4 w-4 text-gray-500" aria-hidden />
          Assigned Providers ({rows.length})
        </h4>
        {canCreate && (
          <button
            type="button"
            onClick={() => setShowWizard(true)}
            className="rounded-md bg-indigo-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-indigo-700"
            data-testid="contact-assign-provider"
          >
            Assign provider
          </button>
        )}
      </div>

      {isLoading && <p className="text-xs text-slate-500">Loading assignments…</p>}
      {!isLoading && (
        <AssignmentsTable
          rows={rows}
          columns={['provider', 'department', 'role', 'priority', 'status', 'startDate']}
          emptyText="No providers assigned"
          onChanged={() => refetch()}
        />
      )}

      {showWizard && (
        <AssignmentWizard
          onClose={() => setShowWizard(false)}
          initial={{ contactId }}
          onCreated={() => refetch()}
        />
      )}
    </section>
  );
}
